import {
	type ChangeEvent,
	type Dispatch,
	type FunctionComponent,
	type HTMLAttributes,
	type SetStateAction,
	useEffect,
	useState
} from 'react';
import { parseValue } from '../../../helpers/general-helper/general-helper';
import {
	addSection,
	newSection,
	removeSection,
	type SqFtInformation,
	type SqFtSection,
	updateInformation
} from '../../../helpers/product-helper/sqft-calculator.helper';
import { StyledButton, TextButton } from '../../buttons';
import { ExpandablePanel } from '../../common-components/expandable-panel/expandable-panel.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { type InputMode, TextInput } from '../../inputs';
import DeleteBinIcon from '../../svgs/delete-bin.svg';

export type SqFtCalculatorProps = {
	information?: SqFtInformation;
	onChange?: Dispatch<SetStateAction<number>>;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Square Footage Calculator Component
 *
 * @param {SqFtCalculatorProps} props
 * @returns {ReactNode}
 */
export const SqFtCalculator: FunctionComponent<SqFtCalculatorProps> = ({ className = '', information, onChange }) => {
	const DEFAULT_CLASSES = `flex flex-column items-center pa2 pa3-ns`;
	const [state, setState] = useState(
		information || {
			sections: [newSection(1)],
			subtotal: 0,
			waste: 0,
			total: 0
		}
	);

	/**
	 * Effect on state change
	 */
	useEffect(() => {
		if (onChange) {
			onChange(state.total);
		}
	}, [onChange, state]);

	return (
		<ExpandablePanel
			heading={`Square Footage Calculator`}
			className={`f5 ${className}`}
			renderCollapsedChildren={true}
			containerClassName={`pa0`}
			startExpanded={false}>
			<div className={`${DEFAULT_CLASSES}`}>
				{state.sections.map((section, index) => (
					<SqFtArea section={section} information={state} key={section.id} isFirst={index === 0} onChange={setState} />
				))}
				<SqFtTotals information={state} onChange={setState} />
			</div>
		</ExpandablePanel>
	);
};

export type SqFtSectionProps = {
	section: SqFtSection;
	information: SqFtInformation;
	onChange: Dispatch<SetStateAction<SqFtInformation>>;
	isFirst: boolean;
	label?: string;
};

/**
 * Square Footage Area Component
 *
 * @param {SqFtSectionProps | SqFtSection} props
 * @returns {ReactNode}
 */
export const SqFtArea: FunctionComponent<SqFtSectionProps> = ({ section, information, onChange, isFirst, label = 'Section' }) => {
	const sectionName = `${label} ${section.id}`;

	/**
	 * Remove Section Handler
	 */
	const onRemoveSection = (): void => {
		onChange(removeSection(section, information));
	};

	/**
	 * Value Change Handler
	 *
	 * @param {ChangeEvent} e
	 */
	const onValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const field = parseValue(e.currentTarget.getAttribute('name') ?? '', e.currentTarget.value);
		onChange(updateInformation(information, { ...section, ...field }));
	};

	return (
		<section className={`flex flex-column mb3 f6 f5-ns`}>
			<header className={`flex flex-row items-center justify-between`}>
				<h4 className={`b mv0`}>{sectionName}</h4>
				{!isFirst && (
					<div className={`w-auto`}>
						<TextButton color="grey" onClick={onRemoveSection}>
							<DeleteBinIcon className={`f4`} data-testid="DeleteBinIcon" />
						</TextButton>
					</div>
				)}
			</header>
			<div className={`flex flex-row items-center justify-between mt3`}>
				<SqFtInput
					id={section.id}
					unit={`ft.`}
					name={`widthFt`}
					ariaLabel={`Square Footage Width for ${sectionName}`}
					onChange={onValueChange}
				/>
				<SqFtInput
					id={section.id}
					unit={`in.`}
					name={`widthIn`}
					ariaLabel={`Width By Inches for ${sectionName}`}
					onChange={onValueChange}
				/>
			</div>
			<div className={`flex flex-row items-center center mt3`}>
				<span>by</span>
			</div>
			<div className={`flex flex-row items-center justify-between mt3`}>
				<SqFtInput
					id={section.id}
					unit={`ft.`}
					name={`heightFt`}
					ariaLabel={`Square Footage Height for ${sectionName}`}
					onChange={onValueChange}
				/>
				<SqFtInput
					id={section.id}
					unit={`in.`}
					name={`heightIn`}
					ariaLabel={`Height By Inches for ${sectionName}`}
					onChange={onValueChange}
				/>
			</div>
			<div className={`flex flex-row items-center justify-between mt3`}>
				<SqFtInput
					id={section.id}
					label={`Waste`}
					unit={`%`}
					unitClass={`mt4`}
					name={`waste`}
					mode={`numeric`}
					placeholder={'10'}
					maxlength={2}
					onChange={onValueChange}
				/>
			</div>
		</section>
	);
};

export type SqFtInputProps = {
	id: number;
	name: string;
	onChange: (e: ChangeEvent<HTMLInputElement>) => void;
	ariaLabel?: string;
	maxlength?: number;
	mode?: InputMode;
	unit?: string;
	unitClass?: string;
	label?: string;
	placeholder?: string;
};

/**
 * Square Footage Input Component
 *
 * @param {SqFtInputProps} props
 * @returns {ReactNode}
 */
export const SqFtInput: FunctionComponent<SqFtInputProps> = ({
	id,
	name,
	ariaLabel,
	maxlength = 6,
	mode = 'decimal',
	unit,
	unitClass = '',
	label,
	placeholder = '',
	onChange
}) => {
	const inputId = `${name}:${id}`;
	ariaLabel = ariaLabel || `Input ${name}`;

	return (
		<div className={`flex flex-row items-center lh-copy mt2 mr3`}>
			{label ? (
				<TextInput
					id={inputId}
					data-testid={name}
					name={name}
					label={label}
					max={maxlength.toString()}
					maxLength={maxlength}
					inputMode={mode}
					placeholder={placeholder}
					onChange={onChange}
				/>
			) : (
				<TextInput
					id={inputId}
					data-testid={name}
					name={name}
					ariaLabel={ariaLabel}
					max={maxlength.toString()}
					maxLength={maxlength}
					inputMode={mode}
					placeholder={placeholder}
					onChange={onChange}
				/>
			)}
			{unit && <span className={`${unitClass} ml2 ml3-ns f6`}>{unit}</span>}
		</div>
	);
};

export type SqFtTotalsProps = {
	information: SqFtInformation;
	onChange: Dispatch<SetStateAction<SqFtInformation>>;
};

/**
 * Square Footage Totals Component
 *
 * @param {SqFtTotalsProps} props
 * @returns {ReactNode}
 */
export const SqFtTotals: FunctionComponent<SqFtTotalsProps> = ({ information, onChange }) => {
	const units = 'sq. ft.';

	/**
	 * Add Section Handler
	 */
	const onAddSection = (): void => {
		onChange(addSection({}, information));
	};

	return (
		<section className={`flex flex-column items-center mb3`}>
			<StyledButton buttonType={`button`} buttonStyle={'PRIMARY'} onClick={onAddSection}>
				Add Section
			</StyledButton>
			<div className={`flex flex-row items-center justify-between f6 f5-ns mt3`}>
				<section className={`flex flex-column mh4`} aria-label={`Subtotal ${information.subtotal} square feet`}>
					<strong>Subtotal</strong>
					<div className={`flex flex-column flex-row-ns mt1`}>
						<strong>{information.subtotal.toFixed(2)}</strong>
						<span className={`ml0 ml2-ns i f6`}>{units}</span>
					</div>
				</section>
				<section className={`flex flex-column mh4`} aria-label={`Total Waste ${information.waste} square feet`}>
					<strong>Waste</strong>
					<div className={`flex flex-column flex-row-ns mt1`}>
						<strong>{information.waste.toFixed(2)}</strong>
						<span className={`ml0 ml2-ns i f6`}>{units}</span>
					</div>
				</section>
				<section className={`flex flex-column mh4`} aria-label={`Total ${information.total} square feet`}>
					<strong>Total</strong>
					<div className={`flex flex-column flex-row-ns mt1`}>
						<strong>{information.total.toFixed(2)}</strong>
						<span className={`ml0 ml2-ns i f6`}>{units}</span>
					</div>
				</section>
			</div>
			<div className={`mt3`}>
				<CloudinaryImage
					options={{ width: 175, height: 254 }}
					publicID={`mediabase/global/fm-sqft-diagram.jpg`}
					description={'Square Footage Diagram'}
				/>
			</div>
		</section>
	);
};
